/**
 * REST client for ACADEMY analytics (F4-C). Same as CRM/billing: data
 * is SERVER-ONLY (no PowerSync), over `@/lib/rest`'s shared transport
 * (which is what knows how to authenticate on web and Tauri), wrapped in TanStack Query.
 *
 * `GET /api/academy/:orgId/metrics` → a single payload with all the sections.
 */
import { createSafeQuery } from "@/lib/api";
import { apiJson } from "@/lib/rest";

const BASE = "/api/academy";

// ─── Tipos (espejo de core-rust db::academy_metrics) ──────────────────────────

export interface StageCount {
  stage: string;
  count: number;
}
export interface EnrollmentPoint {
  month: string;
  leads: number;
  conversions: number;
  enrollments: number;
}
export interface EnrollmentMetrics {
  byStage: StageCount[];
  byMonth: EnrollmentPoint[];
}
export interface RevenuePoint {
  month: string;
  method: string;
  currency: string;
  usdMinor: number;
}
export interface RevenueMetrics {
  byMonth: RevenuePoint[];
  totalUsdMinor: number;
}
export interface AgingBucketAmount {
  bucket: string;
  usdMinor: number;
  count: number;
}
export interface DebtMetrics {
  buckets: AgingBucketAmount[];
  totalUsdMinor: number;
}
export interface CohortRate {
  cohortId: string;
  name: string;
  rate: number;
}
export interface AttendanceMetrics {
  avgRate: number | null;
  worstCohorts: CohortRate[];
  atRiskCount: number;
}
export interface CohortCompletion {
  cohortId: string;
  name: string;
  completionRate: number;
  total: number;
  completed: number;
}
export interface SchemeAvg {
  scheme: string;
  avg: number;
  count: number;
}
export interface MonthCount {
  month: string;
  count: number;
}
export interface AcademicMetrics {
  completionByCohort: CohortCompletion[];
  avgFinalByScheme: SchemeAvg[];
  certificatesByMonth: MonthCount[];
}
export interface MediaMetrics {
  videoCount: number;
  storageBytes: number;
}
export interface EngagementMetrics {
  active7d: number;
  active30d: number;
}
export interface AcademyMetrics {
  orgId: string;
  enrollment: EnrollmentMetrics;
  revenue: RevenueMetrics;
  debt: DebtMetrics;
  attendance: AttendanceMetrics;
  academic: AcademicMetrics;
  media: MediaMetrics;
  engagement: EngagementMetrics;
}

const academyMetricsKey = (orgId: string | undefined) =>
  ["academy", "metrics", orgId ?? "none"] as const;

export const useAcademyMetrics = (orgId: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: academyMetricsKey(orgId()),
    enabled: Boolean(orgId()),
    staleTime: 30_000,
    queryFn: () => apiJson<AcademyMetrics>(`${BASE}/${encodeURIComponent(orgId() ?? "")}/metrics`),
  }));
